import { offerImageSx } from 'components/offers/offerImages';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Button, ButtonBase, Link, Typography } from '@mui/material';
import api from 'utils/api';
import { ImageRecommendationCache } from './ImageRecommendationSession';
import { suggestedImageError } from './suggestedImageErrors';
import ServiceCapacityNotice from 'components/ServiceCapacityNotice';
import { isServicePaused, STOCK_PHOTOS_BUSY, STOCK_PHOTOS_BUSY_MESSAGE, useServiceCooldown } from 'utils/serviceAvailability';

export default function SuggestedImages({ businessId, keywords = [], headline1 = '', headline2 = '', description = '', active, disabled, onSelect }) {
  const sessionCache = useContext(ImageRecommendationCache);
  const fallbackCache = useRef(new Map());
  const cache = sessionCache || fallbackCache.current;
  const [, redraw] = useState(0);
  const [retry, setRetry] = useState(0);
  const entry = cache.get(businessId);
  const loaded = entry?.photos !== undefined;
  const loading = Boolean(entry?.promise);
  const error = entry?.selectionError || entry?.error;
  const { paused } = useServiceCooldown(STOCK_PHOTOS_BUSY);
  const page = entry?.page || 0;
  const pageCount = loaded ? Math.ceil(entry.photos.length / 4) : 0;
  const queryKey = JSON.stringify({ keywords: Array.isArray(keywords) ? keywords : [], headline1, headline2, description });

  useEffect(() => {
    if (!active) return undefined;
    let current = true;
    let cached = cache.get(businessId);
    if (!cached) {
      cached = { page: 0 };
      cache.set(businessId, cached);
      if (isServicePaused(STOCK_PHOTOS_BUSY)) {
        cached.error = { code: STOCK_PHOTOS_BUSY, message: STOCK_PHOTOS_BUSY_MESSAGE };
        redraw((value) => value + 1);
        return undefined;
      }
      cached.promise = api.post(`/offer/${businessId}/images/pexels/recommendations`, JSON.parse(queryKey))
        .then((response) => { cached.photos = response.photos.slice(0, 8); })
        .catch((error) => { cached.error = suggestedImageError(error); })
        .finally(() => { cached.promise = null; });
      redraw((value) => value + 1);
    }
    cached?.promise?.then(() => { if (current) redraw((value) => value + 1); });
    return () => { current = false; };
  }, [active, businessId, queryKey, cache, retry]);

  const changePage = (next) => {
    entry.page = next;
    redraw((value) => value + 1);
  };
  const selectPhoto = async (photo) => {
    if (disabled || isServicePaused(STOCK_PHOTOS_BUSY)) return;
    try {
      await onSelect(photo);
      if (entry.selectionError) {
        entry.selectionError = null;
        redraw((value) => value + 1);
      }
    } catch (error) {
      entry.selectionError = suggestedImageError(error, 'Couldn’t load this image. Please try again.');
      entry.failedPhoto = photo;
      redraw((value) => value + 1);
    }
  };
  const retryRequest = () => {
    if (disabled || isServicePaused(STOCK_PHOTOS_BUSY)) return;
    if (entry?.selectionError) {
      selectPhoto(entry.failedPhoto);
      return;
    }
    cache.delete(businessId);
    setRetry((value) => value + 1);
  };

  return <Box>
    <Typography variant="body2" component="div" sx={{ mb: 1 }}>
      <Link href="https://www.pexels.com" target="_blank" rel="noopener noreferrer">Photos provided by Pexels</Link>
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
      You’re responsible for checking that an image is suitable for your offer and that you have any required permissions.
      {' '}Images must not imply endorsement by people or brands shown.
      {' '}Review the <Link href="https://www.pexels.com/license/" target="_blank" rel="noopener noreferrer">Pexels license</Link>
      {' and '}<Link href="https://www.pexels.com/terms-of-service/" target="_blank" rel="noopener noreferrer">terms of use</Link>.
    </Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
      {loaded && entry.photos.slice(page * 4, page * 4 + 4).map((photo) => <Box key={photo.id}>
        <ButtonBase disabled={disabled || paused} onClick={() => selectPhoto(photo)} aria-label={`Select image: ${photo.description || photo.id}`}
          sx={{ width: '100%', borderRadius: 1, overflow: 'hidden', '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main' } }}>
          <Box component="img" src={photo.thumbnailUrl} alt={photo.description || ''} loading="lazy"
            sx={{ ...offerImageSx, width: '100%' }} />
        </ButtonBase>
        <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>Photo by <Link href={photo.photoUrl} target="_blank" rel="noopener noreferrer">{photo.photographer}</Link></Typography>
      </Box>)}
    </Box>
    {pageCount > 1 && <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5 }}>
      <Button disabled={page === 0 || disabled} onClick={() => changePage(page - 1)}>Previous</Button>
      <Typography variant="caption">Page {page + 1} of {pageCount}</Typography>
      <Button disabled={page + 1 >= pageCount || disabled} onClick={() => changePage(page + 1)}>Next</Button>
    </Box>}
    {loading && <Typography role="status" variant="caption">Loading suggested images…</Typography>}
    {loaded && !entry.photos.length && <Typography variant="body2">No suggested images for this offer yet. Try uploading an image or choosing one of your images.</Typography>}
    {(paused || error?.code === STOCK_PHOTOS_BUSY) && <ServiceCapacityNotice
      action={<Button color="inherit" disabled={disabled || paused} onClick={retryRequest}>Retry</Button>}>
      {STOCK_PHOTOS_BUSY_MESSAGE}
    </ServiceCapacityNotice>}
    {!paused && error && error.code !== STOCK_PHOTOS_BUSY && <Box role="alert">{error.message} <Button disabled={disabled} onClick={retryRequest}>Retry</Button></Box>}
  </Box>;
}
